import { type DocumentNode, gql, useQuery } from '@apollo/client';
import OfflineBoltIcon from '@mui/icons-material/OfflineBolt';
import { Button } from '@mui/material';
import Box from '@mui/material/Box/Box';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import type React from 'react';
import { useState } from 'react';
import AkomiDialog from 'src/components/dialogs/AkomiDialog';
import AkomiDialogActions from 'src/components/dialogs/AkomiDialogActions';
import { skeletonSx } from 'src/components/dialogs/DialogSkeleton';
import { API_PATH, HIRES_FORMAT_SLUG } from 'src/constants';
import { AssetNodeType, formatSet } from 'src/dynamoQueries/assets';
import { SharedAsset } from 'src/dynamoQueries/sharedLinks';
import viewStateVar from 'src/store/viewState';

interface FormatTypeNode {
  slug: string;
  name: string;
}

interface FormatNode {
  id: string;
  filename: string;
  online: boolean;
  fmtType: FormatTypeNode;
}

interface FormatEdge {
  node: FormatNode;
}

interface AssetPermissions {
  hires: boolean;
}

interface SharedAssetQueryResponse {
  sharedAsset: {
    myAssetPermissions: string;
    formatSet: {
      edges: FormatEdge[];
    };
  };
}

const SharedItemDownloadModal: React.FC = () => {
  const [selectedFormat, setSelectedFormat] = useState<string | null>(null);
  const [fSet, setFormatSet] = useState<FormatEdge[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isOpen = !!viewStateVar().sharedItemDownloadDialog;

  const buildQuery = (): DocumentNode => {
    const asn = AssetNodeType(true) as Record<string, unknown>;
    asn.formatSet = formatSet(true);
    (asn.formatSet as Record<string, unknown>).__args = { downloadable: true };
    const q = SharedAsset() as { query: Record<string, unknown> };
    q.query.sharedAsset = { ...(q.query.sharedAsset as object), ...asn };
    (q.query.sharedAsset as Record<string, unknown>).__args = {
      id: viewStateVar().sharedItemDownloadDialog,
    };
    delete q.query.__variables;
    const gQuery = jsonToGraphQLQuery(q, { pretty: true });
    return gql(gQuery);
  };

  const { loading } = useQuery<SharedAssetQueryResponse>(buildQuery(), {
    skip: !isOpen,
    onCompleted: (data) => {
      const permissions: AssetPermissions = JSON.parse(JSON.parse(data.sharedAsset.myAssetPermissions));
      if (!permissions.hires) {
        setFormatSet(
          data.sharedAsset.formatSet.edges.filter((format) => format.node.fmtType.slug !== HIRES_FORMAT_SLUG),
        );
      } else {
        setFormatSet(data.sharedAsset.formatSet.edges);
      }
    },
    onError: (e) => {
      setError(e.message);
    },
  });

  const onClose = (): void => {
    viewStateVar({
      ...viewStateVar(),
      sharedItemDownloadDialog: null,
    });
  };

  const onFormatSelect = (e: SelectChangeEvent<string | null>): void => {
    setError(null);
    setSelectedFormat(e.target.value as string);
  };

  if (!fSet?.length && !loading) {
    return (
      <AkomiDialog open={isOpen} onClose={onClose} title="Download" maxWidth="xs">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}>
          No downloadable formats available.
        </Box>
      </AkomiDialog>
    );
  }

  return (
    <AkomiDialog
      open={isOpen}
      onClose={onClose}
      title="Download"
      maxWidth="xs"
      loading={loading}
      skeleton={
        <Box sx={{ mb: 2 }}>
          <Box sx={{ ...skeletonSx, width: 120, height: 12, mb: '6px' }} />
          <Box sx={{ ...skeletonSx, width: '100%', height: 36 }} />
        </Box>
      }
      actions={
        !loading ? (
          <AkomiDialogActions variant="confirm">
            <Button
              onClick={onClose}
              disabled={!selectedFormat}
              variant="contained"
              color="primary"
              href={`${window.location.origin}${API_PATH}/format/${selectedFormat}/shared/download`}
            >
              Download
            </Button>
          </AkomiDialogActions>
        ) : undefined
      }
    >
      <Box sx={{ mb: 2 }}>
        <FormLabel sx={{ display: 'block', fontSize: '13px', color: 'text.secondary', mb: '6px' }}>
          Select a file format
        </FormLabel>
        <Select fullWidth size="small" value={selectedFormat} onChange={onFormatSelect}>
          {fSet?.map((f) => (
            <MenuItem key={f.node.id} value={f.node.id} disabled={!f.node.online}>
              {!f.node.online && <OfflineBoltIcon sx={{ pr: '7px' }} />}
              {`${f.node.fmtType.name} (${f.node.filename})`}
            </MenuItem>
          ))}
        </Select>
      </Box>
      {error && <Typography color="error">{error}</Typography>}
    </AkomiDialog>
  );
};

export default SharedItemDownloadModal;
